import React from 'react';
import { Link } from 'react-router-dom';

function DressAppearanceGuide({ darkMode }) {
  const containerClass = darkMode 
    ? 'min-h-screen bg-gray-900 text-white' 
    : 'min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 text-gray-800';

  const cardClass = darkMode 
    ? 'bg-gray-800 border-gray-700' 
    : 'bg-white border-gray-200';

  return (
    <div className={containerClass}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">👗</div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            면접 복장 & 첫인상 관리
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            업종별 복장과 첫인상으로 승부하기
          </p>
          <div className={`inline-block px-6 py-3 rounded-full text-sm font-semibold ${darkMode ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800'}`}>
            💡 외모가 실력을 결정하지는 않지만, 기회는 결정할 수 있어요!
          </div>
        </div>

        {/* 첫인상의 중요성 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-teal-600">🎯 첫인상이 면접 결과를 좌우하는 이유</h2>
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-teal-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-teal-600">"첫 3초가 면접의 90%를 결정한다니까요!"</h3>
              <p className="text-lg mb-4">면접관들 대부분이 첫인상을 가장 중요하게 본다고 해요. 더 놀라운 건 첫인상이 3초, 빠르면 0.3초 만에 결정된다는 거예요! 😱</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-teal-500`}>
              <h3 className="text-xl font-bold mb-4 text-teal-600">📊 과학이 증명한 첫인상의 힘</h3>
              <p className="mb-4">유명한 메라비언 법칙에 따르면:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">55%</div>
                  <div className="text-sm">보이는 것 (외모, 표정, 자세)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600">38%</div>
                  <div className="text-sm">들리는 것 (목소리, 톤)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-500">7%</div>
                  <div className="text-sm">말하는 내용</div>
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold text-teal-600">결국 말의 내용보다 어떻게 보이고 들리느냐가 93%를 차지한다는 뜻이에요!</p>
            </div>

            <div>
              <h4 className="font-bold mb-3 text-teal-600">면접관이 속으로 생각하는 것들:</h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-3">
                  <div className="text-lg">🤔</div>
                  <span>"이 사람과 함께 일하고 싶을까?"</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="text-lg">🏢</div>
                  <span>"우리 회사 이미지에 어울릴까?"</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="text-lg">👥</div>
                  <span>"고객 앞에 내세워도 될까?"</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 복장 선택의 황금 법칙 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-orange-600">⚖️ 복장 선택의 절대 법칙</h2>
          
          <div className={`p-6 rounded-xl mb-6 ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
            <h3 className="text-xl font-bold mb-4 text-orange-600">🎯 무조건 보수적으로! 이게 답이에요</h3>
            <p className="mb-4"> <strong>애매하면 정장</strong>이라는 거예요!</p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-bold mb-2 text-orange-600">왜 보수적이 최고일까요?</h4>
                <ul className="space-y-2 ml-4">
                  <li>• "편한 복장"이라고 해도 기준이 사람마다 달라요</li>
                  <li>• 면접관도 보수적일 수 있어요</li>
                  <li>• 과하게 차려입어서 손해 볼 일은 절대 없어요!</li>
                  <li>• 중소기업이라도 꼰대 면접관 많거든요 😅</li>
                </ul>
              </div>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-l-4 border-orange-500`}>
                <h4 className="font-bold mb-2 text-orange-600">"편한 복장 입고 오세요"라고 해도:</h4>
                <ul className="space-y-1 text-sm">
                  <li>• 그냥 정장 입고 가세요!</li>
                  <li>• 최소한 세미정장은 해야 해요</li>
                  <li>• 운동화나 샌들은 절대 금지!</li>
                  <li>• 친구 만날 때 입는 옷은 안 돼요</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 남성 복장 가이드 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-blue-600">👔 남성 복장 이렇게 하세요</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-600">📐 정장이 답입니다</h3>
              
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <h4 className="font-bold mb-3 text-blue-600">색깔 추천 순서:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-blue-900 rounded"></div>
                    <span><strong>네이비</strong> - 가장 무난하고 신뢰감 줘요</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gray-600 rounded"></div>
                    <span><strong>차콜 그레이</strong> - 세련되고 안정적이에요</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-black rounded"></div>
                    <span><strong>블랙</strong> - 격식 있고 전문적이에요 ✅</span>
                  </div>
                </div>
                <p className="mt-3 text-sm text-blue-600">검정도 충분히 좋은 선택이에요! 특히 정장에서는 전혀 문제없어요.</p>
              </div>

              <div className="mt-6">
                <h4 className="font-bold mb-3 text-blue-600">핏은 이렇게:</h4>
                <ul className="space-y-2">
                  <li>• 어깨선 딱 맞게 (제일 중요!)</li>
                  <li>• 소매는 손목뼈 살짝 보일 정도</li>
                  <li>• 바지는 구두에 살짝 닿을 정도</li>
                  <li>• 허리는 주먹 하나 들어갈 정도</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-600">👔 셔츠 & 넥타이는 이렇게</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-blue-500`}>
                  <h4 className="font-bold mb-2">셔츠:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 화이트 무지가 최고예요 (모든 정장과 완벽 매치)</li>
                    <li>• 하늘색도 괜찮아요 (친근하고 신뢰감 있어 보여요)</li>
                    <li>• 패턴 있는 건 피하세요</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-blue-500`}>
                  <h4 className="font-bold mb-2">넥타이:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 너무 넓거나 좁지 않게 (7-8cm 정도)</li>
                    <li>• 길이는 벨트 버클까지</li>
                    <li>• 네이비(성실), 파랑(신뢰), 회색(차분), 버건디(안정감) 추천</li>
                    <li>• 캐릭터나 너무 화려한 건 절대 안 돼요!</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-blue-600">👞 신발과 양말은 이거면 끝</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold mb-2 text-blue-600">신발:</h4>
                  <ul className="space-y-1">
                    <li>• <strong>옥스퍼드</strong>: 가장 안전하고 격식 있어요</li>
                    <li>• <strong>더비</strong>: 조금 캐주얼하지만 면접 OK</li>
                    <li>• 광택 내고, 밑창 상태 체크하고, 냄새도 확인하세요!</li>
                  </ul>
                </div>

                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border-l-4 border-red-500`}>
                  <h4 className="font-bold mb-2 text-red-600">🧦 화이트 양말은 왜 안 될까요?</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>시각적으로 이상한 이유:</strong></p>
                    <ul className="ml-4 space-y-1">
                      <li>• 정장(어두운 색) + 구두(검정/갈색) + 화이트 양말 = 색 조합이 완전 어색해요</li>
                      <li>• 마치 운동복에 정장 재킷 입은 것 같은 느낌</li>
                      <li>• 다리가 중간에 하얗게 끊어져서 보기 이상해요</li>
                    </ul>
                    <p className="mt-3"><strong>✅ 정답은:</strong> 검정 양말, 네이비 양말, 차콜 그레이 양말</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 여성 복장 가이드 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-pink-600">👗 여성 복장 이렇게 하세요</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-pink-600">💼 정장 세트가 최고</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-pink-50'} border-l-4 border-pink-500`}>
                  <h4 className="font-bold mb-2 text-green-600">✅ 추천:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 재킷 + 치마 또는 재킷 + 바지</li>
                    <li>• 자켓 + 블라우스 조합이 가장 안정적</li>
                    <li>• 통일감 있고 전문적인 이미지</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'} border-l-4 border-yellow-500`}>
                  <h4 className="font-bold mb-2 text-yellow-600">⚠️ 원피스:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 나쁘지 않지만 너무 캐주얼해 보일 수 있어요</li>
                    <li>• 꼭 재킷과 함께 입으세요</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-pink-600">📏 치마 vs 바지 고민된다면</h3>
              
              <div className="space-y-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-pink-500`}>
                  <h4 className="font-bold mb-2">치마 입을 때:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 무릎 위아래 5cm (앉았을 때도 체크해보세요!)</li>
                    <li>• 너무 타이트하거나 플레어는 안 돼요</li>
                    <li>• 스타킹은 살구색,커피색,겨울엔 블랙정도</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-pink-500`}>
                  <h4 className="font-bold mb-2">바지 입을 때:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 일자핏, 부츠컷 (스키니진은 금지!)</li>
                    <li>• 활동적이고 당당한 느낌 줘요</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-pink-600">💄 메이크업 & 헤어 팁</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-pink-500`}>
                  <h4 className="font-bold mb-2">메이크업:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 자연스럽게! 너무 진하면 안 돼요</li>
                    <li>• 베이스는 살짝 매트하게 (너무 번들거리지 않게!)</li>
                    <li>• 립은 코랄, 로즈 톤 (빨간 립은 피하세요)</li>
                    <li>• 생기 있는 정도면 충분해요</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-pink-500`}>
                  <h4 className="font-bold mb-2">헤어:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 얼굴 가리지 않게 정리하세요</li>
                    <li>• 긴 머리는 뒤로 묶거나 한쪽으로 넘기기</li>
                    <li>• 화려한 염색이나 과도한 볼륨은 피하세요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 그루밍 & 청결 관리 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-green-600">✨ 깔끔하게 관리하는 법</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-green-600">🚿 기본 중의 기본</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-green-50'} border-l-4 border-green-500`}>
                  <h4 className="font-bold mb-2">면접 전날:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 깔끔하게 샤워하고 완전히 말리기</li>
                    <li>• 손톱 정리 (더러움 제거 필수!)</li>
                    <li>• 코털 체크 (진짜 중요해요!)</li>
                    <li>• 향수를 쓴다면 은은하게만 (2-3번만 뿌리세요)</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-green-50'} border-l-4 border-green-500`}>
                  <h4 className="font-bold mb-2">면접 당일 아침:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 양치질 + 구강청결제 (입냄새 주의!)</li>
                    <li>• 데오드란트 사용하기</li>
                    <li>• 거울 앞에서 최종 체크</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-green-600">💈 남성분들 특별히 주의하세요</h3>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h4 className="font-bold mb-2">면도:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 면접 당일 아침에 하세요 (전날 X, 상처날 수 있어요)</li>
                    <li>• 깔끔하게 젤까지 제거</li>
                    <li>• 콧수염, 구레나룻도 정리</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h4 className="font-bold mb-2">헤어:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 자연스럽고 단정하게</li>
                    <li>• 젤로 고정해도 되지만 번들거리지 않게</li>
                    <li>• 앞머리가 눈 가리면 안 돼요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 바디랭귀지 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-purple-600">🤝 바디랭귀지로 좋은 인상 주기</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">🚪 대기실부터 면접이 시작이에요</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">대기실에서:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 휴대폰 사용 최소화 (게임하면 절대 안 돼요!)</li>
                    <li>• 바른 자세로 앉기</li>
                    <li>• 다른 지원자와 불필요한 대화 피하기</li>
                    <li>• 면접관이 보고 있다고 생각하고 행동하세요</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">면접실 들어갈 때:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 문 두드리고 "들어가겠습니다"</li>
                    <li>• 문 조용히 닫기</li>
                    <li>• 밝은 표정으로 인사</li>
                    <li>• 허락받고 앉기</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">🤝 악수는 이렇게</h3>
              <ul className="space-y-2">
                <li>• 적당한 세기로 (너무 세거나 약하면 이상해요)</li>
                <li>• 2-3초 정도 유지</li>
                <li>• 눈 맞춤 꼭 하세요</li>
                <li>• 손바닥 젖어있으면 절대 안 돼요!</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">🪑 앉는 자세가 중요해요</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">올바른 자세:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 등받이에 완전히 기대지 말기</li>
                    <li>• 손은 무릎 위나 책상에 자연스럽게</li>
                    <li>• 다리 모으고 앞으로 향하게</li>
                    <li>• 발은 바닥에 완전히 닿게</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">표정 관리:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 자연스러운 미소 (억지 웃음 금지!)</li>
                    <li>• 눈 맞춤 70-80% 유지</li>
                    <li>• 고개 끄덕이며 경청하는 모습</li>
                    <li>• 긴장되어도 표정 굳히지 마세요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 절대 금기사항 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-red-600">⚠️ 이건 절대 하지 마세요!</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-red-600">👔 복장 절대 금기</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border-l-4 border-red-500`}>
                  <h4 className="font-bold mb-2 text-red-600">남성:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 화이트 양말 + 정장 (정말 이상해 보여요!)</li>
                    <li>• 운동화 + 정장</li>
                    <li>• 반팔 셔츠 (너무 더운 여름이면 생각해볼수 있지만 추천하지 않아요!)</li>
                    <li>• 청바지 + 정장 재킷</li>
                    <li>• 향수 너무 많이</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border-l-4 border-red-500`}>
                  <h4 className="font-bold mb-2 text-red-600">여성:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 너무 짧은 치마 (앉으면 허벅지 다 보이는)</li>
                    <li>• 속옷 비치는 옷</li>
                    <li>• 너무 화려한 액세서리</li>
                    <li>• 플립플롭, 샌들</li>
                    <li>• 강한 향수</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-red-600">😰 행동으로 감점받는 것들</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="text-2xl text-center">🙅‍♂️</div>
                  <p className="text-sm text-center">면접관보다 먼저 악수 내밀기</p>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl text-center">🪑</div>
                  <p className="text-sm text-center">허락 없이 앉기</p>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl text-center">🦵</div>
                  <p className="text-sm text-center">다리 떨기, 펜 돌리기</p>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl text-center">⌚</div>
                  <p className="text-sm text-center">시계 자주 보기</p>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl text-center">📱</div>
                  <p className="text-sm text-center">휴대폰 소리 나게 하기</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 지자체 대여 사업 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-cyan-600">💰 지자체 면접복장 대여 사업 활용하세요!</h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-cyan-900 bg-opacity-30' : 'bg-cyan-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-cyan-600">🎯 정장값 걱정 없이 면접 준비하세요</h3>
              <p className="mb-4">면접 때문에 수십만원짜리 정장 살 필요 없어요! 전국 많은 지자체에서 <strong>청년 구직자들을 위해 면접복장을 무료로 대여</strong>해주는 사업을 하고 있거든요!</p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-600">📍 어떤 지자체에서 하고 있나요?</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">서울시</div>
                  <div className="text-xs text-cyan-600">취업날개 서비스</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">부천시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">성남시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">안양시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">남양주시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">군산시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">세종시</div>
                </div>
                <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-700' : 'bg-white'} border`}>
                  <div className="text-sm font-semibold">기타 등등...</div>
                </div>
              </div>
              <p className="mt-3 text-sm font-semibold text-cyan-600">정말 많은 곳에서 하고 있어요!</p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-600">🔍 내 지역 찾는 방법</h3>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-cyan-500`}>
                  <h4 className="font-bold mb-2">1. 인터넷 검색:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• "○○시 면접복장 대여"</li>
                    <li>• "○○시 청년 정장 대여"</li>
                    <li>• "○○시 취업지원"</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-cyan-500`}>
                  <h4 className="font-bold mb-2">2. 직접 문의:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 시청 일자리정책과</li>
                    <li>• 지역 고용센터</li>
                    <li>• 청년센터</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-cyan-500`}>
                  <h4 className="font-bold mb-2">3. 확인 사이트:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 온통청년 (youthcenter.go.kr)</li>
                    <li>• 각 지자체 홈페이지</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-600">💡 일반적인 지원 내용</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                  <h4 className="font-bold mb-2">지원 대상:</h4>
                  <p className="text-sm">만18세~39세 해당 지역 거주 청년</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                  <h4 className="font-bold mb-2">지원 품목:</h4>
                  <p className="text-sm">정장, 셔츠/블라우스, 넥타이, 벨트, 구두</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                  <h4 className="font-bold mb-2">대여 기간:</h4>
                  <p className="text-sm">보통 3박4일</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-cyan-50'}`}>
                  <h4 className="font-bold mb-2">이용 비용:</h4>
                  <p className="text-sm">완전 무료! (택배비만 본인 부담)</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-50'} border border-yellow-300`}>
              <h3 className="text-xl font-bold mb-4 text-yellow-600">⚠️ 꼭 확인하세요!</h3>
              <div className="space-y-3">
                <p className="font-bold">📅 매년 사업 내용이 달라져요!</p>
                <ul className="space-y-1 text-sm">
                  <li>• 지원 조건, 횟수, 방법 등이 변경될 수 있어요</li>
                  <li>• 예산 상황에 따라 중단되거나 확대될 수 있어요</li>
                  <li>• <strong>면접 전에 미리 해당 지자체에 확인 필수!</strong></li>
                </ul>
                <div className="mt-4">
                  <p className="font-bold mb-2">📞 문의할 때 이렇게 물어보세요:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• "올해 청년 면접복장 대여 사업 하나요?"</li>
                    <li>• "신청 방법과 조건이 어떻게 되나요?"</li>
                    <li>• "몇 회까지 이용 가능한가요?"</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-600">💪 마지막 한마디</h3>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-cyan-900 bg-opacity-30' : 'bg-cyan-50'} text-center`}>
                <p className="text-lg font-bold mb-2">"정장값 아끼고, 면접 준비에 집중하세요!"</p>
                <p className="text-sm">이런 좋은 제도가 있는데 몰라서 못 쓰면 정말 아깝잖아요! 여러분이 사는 지역에서도 분명 이런 사업이 있을 거예요.</p>
                <p className="text-sm font-bold mt-2 text-cyan-600">매년 사업이 달라지니까 면접 전에 꼭 확인해보시고, 적극 활용하세요!</p>
              </div>
            </div>
          </div>
        </div>

        {/* 최종 체크리스트 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-indigo-600">💡 최종 체크리스트</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">✅ 면접 전날</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>옷 다림질 완료</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>신발 광택내기</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>시계, 벨트 준비</span>
                  </label>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>헤어컷 (1-2일 전에)</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>손톱 정리</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">✅ 면접 당일 아침</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>샤워 + 양치질</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>면도/메이크업</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>향수 적당히</span>
                  </label>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>거울 체크</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>여분 휴지 챙기기</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">✅ 면접장 도착 후</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>화장실에서 마지막 체크</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>휴대폰 무음</span>
                  </label>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>바른 자세로 대기</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span>밝은 표정 준비</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 마무리 메시지 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg text-center`}>
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold mb-4 text-teal-600">마무리 메시지</h2>
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-teal-50'} mb-6`}>
            <p className="text-xl font-bold mb-4 text-teal-600">
              "외모가 실력을 결정하지는 않지만, 기회는 결정할 수 있어요!"
            </p>
            <p className="mb-4">
              첫인상은 여러분의 전문성과 성실함을 보여주는 첫 번째 기회예요.
            </p>
            <div className="space-y-2">
              <p className="font-semibold">핵심 3가지만 기억하세요:</p>
              <div className="flex flex-wrap justify-center gap-4 mt-3">
                <span className={`px-4 py-2 rounded-full ${darkMode ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800'} font-semibold`}>
                  1. 보수적으로 - 애매하면 정장!
                </span>
                <span className={`px-4 py-2 rounded-full ${darkMode ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800'} font-semibold`}>
                  2. 깔끔하게 - 청결이 제일 중요!
                </span>
                <span className={`px-4 py-2 rounded-full ${darkMode ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800'} font-semibold`}>
                  3. 정중하게 - 예의가 기본!
                </span>
              </div>
            </div>
            <p className="mt-4 font-semibold text-teal-600">
              여러분의 진짜 실력을 보여줄 기회를 만들어보세요! 완벽한 준비로 자신감 가지고 면접 보러 가세요! 🎯✨
            </p>
          </div>

          {/* 연습 포인트 */}
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-teal-900 bg-opacity-30' : 'bg-teal-100'} mb-6`}>
            <p className="text-sm font-semibold text-teal-600">연습 포인트:</p>
            <ul className="text-sm mt-2 space-y-1">
              <li>• 면접 전에 거울 앞에서 전체적인 모습 체크하기</li>
              <li>• 지자체 면접복장 대여 사업 미리 알아보기</li>
              <li>• 보수적으로 준비해서 실수 없이 첫인상 만들기</li>
              <li>• 자신감 있는 자세와 밝은 표정으로!</li>
            </ul>
          </div>
        </div>

        {/* 네비게이션 버튼 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/guide-success" 
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 text-center ${
              darkMode 
                ? 'bg-gray-700 text-white hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            ← 면접성공가이드 메인으로
          </Link>
          <Link 
            to="/" 
            className="px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 text-center"
          >
            🏠 홈으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DressAppearanceGuide;